import { formatUserFacingError } from '@/lib/errors'
import React, { useEffect, useMemo, useState } from 'react'
import { GripVertical, Pencil, Search, Trash2 } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { UserChip } from '@/components/ui/user-chip'
import { useAdminPositions } from '../hooks/useAdminPositions'
import { useAdminCompany } from '../hooks/useAdminCompany'
import { adminPositionsService } from '../services/admin.positions.service'
import { adminCompanyService } from '../services/admin.company.service'
import { AdminMemberEditModal } from './AdminMemberEditModal'
import type { PositionDocument, CompanyMemberDocument } from '@/types'

type Member = CompanyMemberDocument & { id: string }
type Position = PositionDocument & { id: string }

interface SortablePositionRowProps {
  position: Position
  onEdit: (position: Position) => void
  onDelete: (position: Position) => void
}

function SortablePositionRow({ position, onEdit, onDelete }: SortablePositionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: position.id,
  })

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <TableCell className="w-8 pr-0">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{position.title}</TableCell>
      <TableCell>
        <Badge variant={position.isActive ? 'default' : 'secondary'}>
          {position.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end">
          <MultiButton
            variant="outline"
            size="sm"
            items={[
              { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => onEdit(position) },
              { id: 'delete', icon: Trash2, label: 'Delete', onClick: () => onDelete(position), className: 'text-destructive hover:text-destructive' },
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

interface SortableGroupSectionProps {
  groupName: string
  positions: Position[]
  sensors: ReturnType<typeof useSensors>
  onPositionDragEnd: (groupName: string, event: DragEndEvent) => void
  onEdit: (position: Position) => void
  onDelete: (position: Position) => void
}

function SortableGroupSection({
  groupName,
  positions,
  sensors,
  onPositionDragEnd,
  onEdit,
  onDelete,
}: SortableGroupSectionProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: groupName,
  })

  return (
    <React.Fragment>
      <TableRow
        ref={setNodeRef}
        className="bg-muted/40 hover:bg-muted/40"
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        <TableCell className="w-8 pr-0">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </TableCell>
        <TableCell colSpan={3} className="py-1.5 px-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {groupName}
          </span>
        </TableCell>
      </TableRow>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => onPositionDragEnd(groupName, e)}
      >
        <SortableContext items={positions.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          {positions.map((position) => (
            <SortablePositionRow
              key={position.id}
              position={position}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </SortableContext>
      </DndContext>
    </React.Fragment>
  )
}

interface SortableRowProps {
  member: Member
  deletingId: string | null
  searchActive: boolean
  onEdit: (id: string) => void
  onDelete: (member: Member) => void
}

function SortableMemberRow({ member, deletingId, searchActive, onEdit, onDelete }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.id,
  })

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <TableCell className="w-8 pr-0">
        {!searchActive && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
      </TableCell>
      <TableCell>
        <UserChip
          user={{ displayName: member.name, photoURL: member.squarePhotoUrl ?? member.photoUrl ?? null }}
          size="sm"
          variant="inline"
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {member.roles.map((role) => (
            <Badge key={role} variant="secondary">{role}</Badge>
          ))}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={member.isActive ? 'default' : 'outline'}>
          {member.isActive ? 'Active' : 'Alumni'}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={member.isPublished ? 'default' : 'secondary'}>
          {member.isPublished ? 'Published' : 'Draft'}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end">
          <MultiButton
            variant="outline"
            size="sm"
            items={[
              { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => onEdit(member.id) },
              {
                id: 'delete',
                icon: Trash2,
                label: 'Delete',
                onClick: () => onDelete(member),
                disabled: deletingId === member.id,
                className: 'text-destructive hover:text-destructive',
              },
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

export function AdminConfigPage() {
  // ─── Positions ──────────────────────────────────────────────────────────────
  const {
    positions,
    loading: posLoading,
    error: posError,
    createPosition,
    updatePosition,
    deletePosition,
    refresh: refreshPositions,
  } = useAdminPositions()

  const [orderedPositions, setOrderedPositions] = useState<Position[]>([])
  const [groupOrder, setGroupOrder] = useState<string[]>([])
  const [savingPositionOrder, setSavingPositionOrder] = useState(false)
  const [positionOrderError, setPositionOrderError] = useState<string | null>(null)

  const { grouped, ungrouped } = useMemo(() => {
    const grouped: Record<string, Position[]> = {}
    const ungrouped: Position[] = []
    for (const pos of orderedPositions) {
      if (pos.group) {
        if (!grouped[pos.group]) grouped[pos.group] = []
        grouped[pos.group]!.push(pos)
      } else {
        ungrouped.push(pos)
      }
    }
    return { grouped, ungrouped }
  }, [orderedPositions])

  const [posFormOpen, setPosFormOpen] = useState(false)
  const [posEditTarget, setPosEditTarget] = useState<(PositionDocument & { id: string }) | null>(null)
  const [posTitleInput, setPosTitleInput] = useState('')
  const [posGroupInput, setPosGroupInput] = useState('')
  const [posSaving, setPosSaving] = useState(false)
  const [posDeleteOpen, setPosDeleteOpen] = useState(false)
  const [posDeleteTarget, setPosDeleteTarget] = useState<(PositionDocument & { id: string }) | null>(null)
  const [posDeleting, setPosDeleting] = useState(false)

  // ─── Members ────────────────────────────────────────────────────────────────
  const {
    members,
    loading: membersLoading,
    error: membersError,
    deleteMember,
    refresh,
  } = useAdminCompany()

  const [orderedMembers, setOrderedMembers] = useState<Member[]>([])
  const [savingOrder, setSavingOrder] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)

  const [memberDeleteOpen, setMemberDeleteOpen] = useState(false)
  const [memberDeleteTarget, setMemberDeleteTarget] = useState<Member | null>(null)
  const [memberDeleting, setMemberDeleting] = useState(false)

  const [memberModalOpen, setMemberModalOpen] = useState(false)
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null)
  const [memberSearch, setMemberSearch] = useState('')

  const filteredMembers = memberSearch.trim()
    ? orderedMembers.filter((m) => {
        const q = memberSearch.toLowerCase()
        return m.name.toLowerCase().includes(q) || m.roles.some((r) => r.toLowerCase().includes(q))
      })
    : orderedMembers

  // Keep local order in sync with hook data, sorted by order field
  useEffect(() => {
    setOrderedPositions([...positions].sort((a, b) => a.order - b.order))
  }, [positions])

  useEffect(() => {
    const seen = new Set<string>()
    const order: string[] = []
    for (const pos of orderedPositions) {
      if (pos.group && !seen.has(pos.group)) {
        seen.add(pos.group)
        order.push(pos.group)
      }
    }
    setGroupOrder(order)
  }, [orderedPositions])

  useEffect(() => {
    setOrderedMembers([...members].sort((a, b) => a.order - b.order))
  }, [members])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // ─── Positions handlers ─────────────────────────────────────────────────────
  function openPosAddForm() {
    setPosEditTarget(null)
    setPosTitleInput('')
    setPosGroupInput('')
    setPosFormOpen(true)
  }

  function openPosEditForm(position: PositionDocument & { id: string }) {
    setPosEditTarget(position)
    setPosTitleInput(position.title)
    setPosGroupInput(position.group ?? '')
    setPosFormOpen(true)
  }

  async function handleGroupDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = groupOrder.indexOf(active.id as string)
    const newIndex = groupOrder.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return

    const newGroupOrder = arrayMove(groupOrder, oldIndex, newIndex)
    setGroupOrder(newGroupOrder)

    const reordered = [
      ...newGroupOrder.flatMap((g) => grouped[g] ?? []),
      ...ungrouped,
    ]
    setOrderedPositions(reordered)
    setSavingPositionOrder(true)
    setPositionOrderError(null)

    try {
      await Promise.all(
        reordered.map((pos, i) =>
          adminPositionsService.updatePosition(pos.id, { order: i + 1 }),
        ),
      )
      refreshPositions()
    } catch (err) {
      setPositionOrderError(formatUserFacingError(err, 'Failed to save order'))
      setGroupOrder(groupOrder)
      setOrderedPositions(orderedPositions)
    } finally {
      setSavingPositionOrder(false)
    }
  }

  async function handlePositionDragEndInGroup(groupName: string | null, event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const inGroup = orderedPositions.filter((p) =>
      groupName === null ? !p.group : p.group === groupName,
    )
    const oldIndex = inGroup.findIndex((p) => p.id === active.id)
    const newIndex = inGroup.findIndex((p) => p.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reorderedGroup = arrayMove(inGroup, oldIndex, newIndex)
    let groupIdx = 0
    const reordered = orderedPositions.map((p) => {
      const belongs = groupName === null ? !p.group : p.group === groupName
      return belongs ? (reorderedGroup[groupIdx++] ?? p) : p
    })

    setOrderedPositions(reordered)
    setSavingPositionOrder(true)
    setPositionOrderError(null)

    try {
      await Promise.all(
        reordered.map((pos, i) =>
          adminPositionsService.updatePosition(pos.id, { order: i + 1 }),
        ),
      )
      refreshPositions()
    } catch (err) {
      setPositionOrderError(formatUserFacingError(err, 'Failed to save order'))
      setOrderedPositions(orderedPositions)
    } finally {
      setSavingPositionOrder(false)
    }
  }

  async function handlePosSave() {
    const trimmedTitle = posTitleInput.trim()
    if (!trimmedTitle) return
    const trimmedGroup = posGroupInput.trim() || undefined
    setPosSaving(true)
    try {
      if (posEditTarget) {
        await updatePosition(posEditTarget.id, { title: trimmedTitle, group: trimmedGroup ?? null })
      } else {
        await createPosition(trimmedTitle, orderedPositions.length + 1, trimmedGroup)
      }
      setPosFormOpen(false)
    } finally {
      setPosSaving(false)
    }
  }

  async function handlePosDelete() {
    if (!posDeleteTarget) return
    setPosDeleting(true)
    try {
      await deletePosition(posDeleteTarget.id)
      setPosDeleteOpen(false)
      setPosDeleteTarget(null)
    } finally {
      setPosDeleting(false)
    }
  }

  // ─── Members handlers ────────────────────────────────────────────────────────
  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = orderedMembers.findIndex((m) => m.id === active.id)
    const newIndex = orderedMembers.findIndex((m) => m.id === over.id)
    const reordered = arrayMove(orderedMembers, oldIndex, newIndex)

    setOrderedMembers(reordered)
    setSavingOrder(true)
    setOrderError(null)

    try {
      await Promise.all(
        reordered.map((member, index) =>
          adminCompanyService.updateMember(member.id, { order: index + 1 }),
        ),
      )
      refresh()
    } catch (err) {
      setOrderError(formatUserFacingError(err, 'Failed to save order'))
    } finally {
      setSavingOrder(false)
    }
  }

  async function handleMemberDelete() {
    if (!memberDeleteTarget) return
    setMemberDeleting(true)
    try {
      await deleteMember(memberDeleteTarget.id)
      setMemberDeleteOpen(false)
      setMemberDeleteTarget(null)
    } finally {
      setMemberDeleting(false)
    }
  }

  return (
    <div className="space-y-8">

      {/* ─── Positions ──────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">Positions</h2>
            {savingPositionOrder && (
              <p className="text-xs text-muted-foreground">Saving order…</p>
            )}
          </div>
          <Button onClick={openPosAddForm}>Add Position</Button>
        </div>

        {(posError ?? positionOrderError) && (
          <p className="text-sm text-destructive">{posError ?? positionOrderError}</p>
        )}

        {posLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <MultiButtonGroup>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => void handleGroupDragEnd(e)}
                >
                  <SortableContext items={groupOrder} strategy={verticalListSortingStrategy}>
                    <TableBody>
                      {orderedPositions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                            No positions yet. Add one to get started.
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {groupOrder.map((groupName) => (
                            <SortableGroupSection
                              key={groupName}
                              groupName={groupName}
                              positions={grouped[groupName] ?? []}
                              sensors={sensors}
                              onPositionDragEnd={handlePositionDragEndInGroup}
                              onEdit={openPosEditForm}
                              onDelete={(p) => { setPosDeleteTarget(p); setPosDeleteOpen(true) }}
                            />
                          ))}
                          {ungrouped.length > 0 && (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(e) => void handlePositionDragEndInGroup(null, e)}
                            >
                              <SortableContext
                                items={ungrouped.map((p) => p.id)}
                                strategy={verticalListSortingStrategy}
                              >
                                {ungrouped.map((position) => (
                                  <SortablePositionRow
                                    key={position.id}
                                    position={position}
                                    onEdit={openPosEditForm}
                                    onDelete={(p) => { setPosDeleteTarget(p); setPosDeleteOpen(true) }}
                                  />
                                ))}
                              </SortableContext>
                            </DndContext>
                          )}
                        </>
                      )}
                    </TableBody>
                  </SortableContext>
                </DndContext>
              </MultiButtonGroup>
            </Table>
          </div>
        )}
      </section>

      <Separator />

      {/* ─── Members ────────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Members</h2>
            {savingOrder && (
              <p className="text-xs text-muted-foreground">Saving order…</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or role…"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="pl-8 w-56"
              />
            </div>
            <Button onClick={() => { setEditingMemberId(null); setMemberModalOpen(true) }}>
              Add Member
            </Button>
          </div>
        </div>

        {(membersError ?? orderError) && (
          <p className="text-sm text-destructive">{membersError ?? orderError}</p>
        )}

        {membersLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => void handleDragEnd(e)}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Name</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <MultiButtonGroup>
                  <SortableContext
                    items={orderedMembers.map((m) => m.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <TableBody>
                      {orderedMembers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                            No members yet. Add one to get started.
                          </TableCell>
                        </TableRow>
                      ) : filteredMembers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                            No members match your search.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredMembers.map((member) => (
                          <SortableMemberRow
                            key={member.id}
                            member={member}
                            searchActive={!!memberSearch.trim()}
                            deletingId={memberDeleting ? memberDeleteTarget?.id ?? null : null}
                            onEdit={(id) => { setEditingMemberId(id); setMemberModalOpen(true) }}
                            onDelete={(m) => { setMemberDeleteTarget(m); setMemberDeleteOpen(true) }}
                          />
                        ))
                      )}
                    </TableBody>
                  </SortableContext>
                </MultiButtonGroup>
              </Table>
            </DndContext>
          </div>
        )}
      </section>

      {/* ─── Dialogs ────────────────────────────────────────────────────────── */}
      <Dialog open={posFormOpen} onOpenChange={setPosFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{posEditTarget ? 'Edit Position' : 'Add Position'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="position-title">Title</Label>
            <Input
              id="position-title"
              placeholder="e.g. Director of Community Engagement"
              value={posTitleInput}
              onChange={(e) => setPosTitleInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handlePosSave() }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="position-group">
              Group <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="position-group"
              placeholder="e.g. Artistic, Production, Community"
              value={posGroupInput}
              onChange={(e) => setPosGroupInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handlePosSave() }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPosFormOpen(false)}>Cancel</Button>
            <Button onClick={() => void handlePosSave()} disabled={!posTitleInput.trim() || posSaving}>
              {posSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={posDeleteOpen} onOpenChange={setPosDeleteOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Position</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">{posDeleteTarget?.title}</span>?{' '}
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPosDeleteOpen(false); setPosDeleteTarget(null) }}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handlePosDelete()} disabled={posDeleting}>
              {posDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminMemberEditModal
        open={memberModalOpen}
        memberId={editingMemberId}
        onClose={() => setMemberModalOpen(false)}
        onSaved={refresh}
      />

      <Dialog open={memberDeleteOpen} onOpenChange={setMemberDeleteOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Member</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">{memberDeleteTarget?.name}</span>?{' '}
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMemberDeleteOpen(false); setMemberDeleteTarget(null) }}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleMemberDelete()} disabled={memberDeleting}>
              {memberDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
